import api from "@/lib/api"
import Cookies from "js-cookie"
import axios from 'axios';

function getAuthHeader() {
  const accessToken = Cookies.get('accessToken')
  if (!accessToken) throw new Error('No access token found')
  return { Authorization: `Bearer ${accessToken}` }
}

export async function getTripsByMonthYear(month: number, year: number) {
  try {
    const response = await api.get(`/trip`, {
      params: { month, year, },
    })
    return response.data
  } catch (error) {
    throw error
  }
}

export async function getRoutes() {
  try {
    const response = await api.get(`/route`)
    return response.data
  } catch (error) {
    throw error
  }
}

export async function getFerry() {
  try {
    const response = await api.get(`/ferry`)
    return response.data
  } catch (error) {
    throw error
  }
}

export async function getDayTrips(date: string) {
  try {
    const response = await api.get(`/trip/day-trips`, { params: { date } })
    return response.data
  } catch (error) {
    throw error
  }
}

export async function postTrip(tripData: {
  ferryId: string,
  routeId: string,
  time: string,
  recurrenceType: 'SINGLE' | 'WEEKLY',
  weekDays?: string[],
  specificDates?: string[],
}) {
  try {
    const response = await api.post(`/trip`, tripData, { headers: getAuthHeader() })
    return response.data
  } catch (error) {
    throw error
  }
}

export async function updateTripTime(tripId: string, time: string) {
  try {
    const response = await api.put(`/trip/${tripId}`, { time }, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function deleteTrip(tripId: string, deleteAllFuture?: boolean) {
  try {
    const url = deleteAllFuture
      ? `/trip/${tripId}?occurrence=all`
      : `/trip/${tripId}`;
    const response = await api.delete(url, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Cancel trip
export async function cancelTrip(tripData: {
  tripId: string;
  reason: string;
  description: string;
}) {
  try {
    const response = await api.post('/trip/cancel', tripData, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Add weather delay to trip
export async function addWeatherDelay(delayData: {
  tripId: string;
  duration: string;
  weatherCondition: string;
  message: string;
}) {
  try {
    const response = await api.post('/trip/delay', delayData, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Fetch fares from /fare endpoint
export async function viewFares() {
  try {
    const response = await api.get('/fare');
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || 'Failed to fetch fares.',
      data: null,
    };
  }
}

// Edit fares via POST /fare
export async function editFares(fares: { type: string; price: string }[]) {
  try {
    const response = await api.post('/fare', { fares }, { headers: getAuthHeader() });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || 'Failed to update fares.',
      data: null,
    };
  }
}

// Search trips with complex query parameters
export async function searchTrips(params: {
  route: string;
  passenger: {
    adult: number;
    senior: number;
    child: number;
    special_assistance: boolean;
    special_assistance_reason?: string;
  };
  vehicle: {
    height: string;
    length: string;
    vehicle_return: boolean;
    type?: string;
  };
  departure: string;
  returning?: string;
  round: 'one' | 'two';
}) {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('route', params.route);
    queryParams.append('passenger', JSON.stringify(params.passenger));
    queryParams.append('vehicle', JSON.stringify(params.vehicle));
    queryParams.append('departure', params.departure);
    if (params.returning && params.round === 'two') {
      queryParams.append('returning', params.returning);
    }
    queryParams.append('round', params.round);

    const response = await api.get(`/trip/search?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Get trip details by trip ID
export async function getTripDetails(tripId: string) {
  try {
    const response = await api.get(`/trip/details/${tripId}`, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    throw error;
  }
}
